import React, { useMemo } from 'react';
import { find, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { Alert, Box, Button, CardContent, FilePdfIcon, Grid, Link, Paper, Typography } from '@availity/element';

import { AvailityHeaderLogo, Footer, MessageDisplay } from '@/components';
import { TwoFAIcon01 } from '@/static';
import { useFRCallback } from '@/hooks';

const faqLink = 'https://www.availity.com/documents/learning/QRG_AP_2step_Providers.pdf';

const TwoFAConfirmNumber = ({ ...props }) => {
  const { stepToRender, setCurrentStep, hotpType } = props;
  const {
    callbacks = [],
    confirmationCallbackIndex,
    confirmationCallbackOptions,
    textOutputCallback,
  } = useFRCallback(stepToRender);

  const altHotpType = useMemo(() => {
    let output;

    switch (hotpType) {
      case 'sms': {
        output = 'tts';
        break;
      }
      case 'tts': {
        output = 'sms';
        break;
      }
      default:
    }

    return output;
  }, [hotpType]);

  const deviceNumber = find(
    textOutputCallback?.output,
    (outputObj) => outputObj?.name?.toLowerCase() === 'message'
  )?.value;

  const handleNavigation = (navigation) => {
    // options should be Next, Back, or Switch
    const nextStep = { ...stepToRender };
    const newCallbacks = [...callbacks];

    // set the confirmation callback input based off the index of the selected button navigation in the callback
    let selectedOptionIndex;
    if (navigation?.toLowerCase() === 'back') {
      selectedOptionIndex = findIndex(confirmationCallbackOptions, (button) => button?.toLowerCase() === 'back');
    } else if (navigation?.toLowerCase() === 'next') {
      selectedOptionIndex = findIndex(confirmationCallbackOptions, (button) => button?.toLowerCase() === 'next');
    } else if (navigation?.toLowerCase() === 'switch') {
      selectedOptionIndex = findIndex(confirmationCallbackOptions, (button) => button?.toLowerCase() === 'switch');
    }

    newCallbacks[confirmationCallbackIndex].input[0] = {
      ...newCallbacks[confirmationCallbackIndex].input[0],
      value: selectedOptionIndex,
    };

    // set the new callbacks in the next step
    Object.assign(nextStep.payload.callbacks, newCallbacks);

    // update the currentStep to trigger navigation
    setCurrentStep({ ...nextStep });
  };

  return (
    <Grid
      id="2fa-start-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        id="2fa-confirm-number-page-content-grid"
        container
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        mt="auto"
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '750px', lg: '1000px' } }}
          >
            <CardContent>
              <Grid display="flex" flexDirection="row" flexWrap="wrap">
                <Grid size={{ sm: 12, md: 4 }} sx={{ marginBottom: { xs: 2, md: 0 }, paddingRight: 2 }}>
                  <Box textAlign="center" mb={2}>
                    <img
                      src={TwoFAIcon01}
                      alt="2FA Icon"
                      style={{
                        maxWidth: '45%',
                      }}
                    />
                  </Box>
                  <Typography variant="h5" component="h3" mb={2}>
                    What is 2-step authentication?
                  </Typography>
                  <Typography variant="subtitle1">
                    2-step authentication protects your account by asking you for two pieces of information: your
                    username & password combination plus a six digit code that will be sent to you.
                  </Typography>
                  <Box mt={3}>
                    <Link
                      href={faqLink}
                      target="_blank"
                      loadApp={false}
                      rel="noopener noreferrer"
                      aria-label="Get answers to common questions (opens PDF in a new window)"
                    >
                      Answers to common questions <FilePdfIcon fontSize="small" />
                    </Link>
                  </Box>
                </Grid>
                <Grid
                  display="flex"
                  flexDirection="column"
                  size={{ md: 8 }}
                  sx={{ borderLeft: { sm: 0, md: 1 }, borderColor: { md: '#D7DAE0' } }}
                >
                  <Box pl={2}>
                    <Typography variant="h1" mb={1}>
                      2-Step Authentication
                    </Typography>
                    <Typography variant="h4" component="h2" mb={2}>{`We will ${
                      hotpType === 'sms' ? 'text' : 'call'
                    } you with a code`}</Typography>
                  </Box>
                  <Grid display="flex" flexDirection="column" flexGrow={1} pl={2} pt={2}>
                    <Typography variant="subtitle1" mb={2}>{`We will ${
                      hotpType === 'sms' ? 'text' : 'call'
                    } you at this number:`}</Typography>
                    <Typography variant="h4" mb={2}>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {deviceNumber}
                      </Box>
                    </Typography>
                    <Typography variant="subtitle1" mb={3}>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>
                        Note:
                      </Box>{' '}
                      Standard text message and data rates may apply.
                    </Typography>
                    <Box mb={5}>
                      <Button color="tertiary" size="small" onClick={() => handleNavigation('switch')}>
                        {`${altHotpType === 'sms' ? 'Text' : 'Call'} me instead.`}
                      </Button>
                    </Box>
                    {hotpType === 'sms' && (
                      <Box mb={5}>
                        <Alert severity="info" padding={2}>
                          If you do not receive your code within 10 minutes, your cell carrier&apos;s spam software may
                          be blocking your message.
                        </Alert>
                      </Box>
                    )}
                    <Box mb={2}>
                      <MessageDisplay />
                    </Box>
                    <Box textAlign="right" mt="auto">
                      <Button color="secondary" onClick={() => handleNavigation('back')}>
                        Back
                      </Button>{' '}
                      <Button color="primary" sx={{ marginLeft: 1 }} onClick={() => handleNavigation('next')}>
                        Continue
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

TwoFAConfirmNumber.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  hotpType: PropTypes.string.isRequired,
};

export default TwoFAConfirmNumber;
